// CARO. Casa de estilo — shipping zone / rate engine (configurable, no invented
// final prices). Rates below are PLACEHOLDERS clearly marked for Carolina to
// confirm real carrier pricing before launch (see docs/launch-checklist.md).

import type { Product } from '@/types';
import { isSoldOut } from '@/lib/catalog/availability';

export interface ShippingRate {
  id: string;
  label: string;
  /** Flat rate in integer cents applied when the zone matches. */
  amountCents: number;
  /** Free above this subtotal (cents). null = no free-shipping threshold. */
  freeThresholdCents: number | null;
  /** PLACEHOLDER — replace with carrier-verified pricing. */
  provisional: boolean;
}

export interface ShippingZone {
  id: string;
  label: string;
  /** ISO-3166-1 alpha-2 country codes included in this zone. */
  countries: string[];
  rate: ShippingRate;
  enabled: boolean;
}

/**
 * Eurozone (countries whose official currency is the euro). Free shipping is
 * offered ONLY inside the eurozone (over the threshold below) — never to
 * non-euro Europe (CH, NO, SE, DK, PL, CZ…) nor internationally. This is a
 * business policy in code (like the tier multipliers), not per-zone config,
 * because the eurozone cuts across the España + Europa shipping zones.
 * NOTE: add 'BG' here if/when Bulgaria's euro adoption is confirmed.
 */
export const EUROZONE_COUNTRIES = new Set([
  'AT', 'BE', 'HR', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV',
  'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES',
]);

export function isEurozone(country: string): boolean {
  return EUROZONE_COUNTRIES.has(country.toUpperCase());
}

/**
 * Free-shipping threshold (integer cents) for eurozone destinations. Applies to
 * the standard/beauty subtotal only (large pieces carry their own per-piece
 * delivery — see src/lib/cart). Policy in code so it is explicit and tested.
 */
export const FREE_SHIPPING_THRESHOLD_CENTS = 5000; // 50 €

/**
 * TEMPORARY fuel surcharge on PAID shipping, in percent (owner 2026-10-09: 35 %; 2026-11-03: 20 %;
 * carriers raised prices with fuel costs). Set to 0 to switch it off
 * everywhere: totals, the checkout breakdown line, emails and the
 * announcement-bar notice all key off this one value. Review it and lower it
 * once transport prices normalise (memory project-caro-backlog).
 * Free shipping (eurozone ≥ 50 €) stays free: 0 is never surcharged.
 */
export const FUEL_SURCHARGE_PERCENT = 20;

/**
 * Apply the fuel surcharge to one shipping amount: +percent, rounded UP to the
 * next x,95 € (4,95 → 6,95; 14,95 → 20,95; 29,95 → 40,95). Returns the amount
 * unchanged when it is 0 (free) or the surcharge is off.
 */
export function applyFuelSurcharge(
  cents: number,
  percent: number = FUEL_SURCHARGE_PERCENT,
): number {
  if (cents <= 0 || percent <= 0) return cents;
  const raised = Math.ceil((cents * (100 + percent)) / 100);
  return Math.ceil((raised - 95) / 100) * 100 + 95;
}

// Three tiers: España, Europa continental, Internacional (catch-all "*").
// Order matters: a specific country match wins; the "*" zone is the fallback.
// Rates are PLACEHOLDERS (provisional) — confirm with the carrier.
export const SHIPPING_ZONES: ShippingZone[] = [
  {
    id: 'es',
    label: 'España',
    countries: ['ES'],
    enabled: true,
    rate: {
      id: 'es-standard',
      label: 'Envío España',
      amountCents: 495,
      // Free shipping is decided by eurozone membership (see calculateShipping),
      // not per zone, so no per-zone threshold here.
      freeThresholdCents: null,
      provisional: true,
    },
  },
  {
    id: 'europe',
    label: 'Europa continental',
    countries: [
      'PT', 'FR', 'DE', 'IT', 'NL', 'BE', 'LU', 'AT', 'IE', 'FI', 'SE', 'DK',
      'PL', 'CZ', 'SK', 'SI', 'HR', 'HU', 'RO', 'BG', 'GR', 'EE', 'LV', 'LT',
      'MT', 'CY', 'CH', 'NO', 'IS', 'LI', 'MC', 'AD', 'SM',
    ],
    enabled: true,
    rate: {
      id: 'europe-standard',
      label: 'Envío Europa',
      amountCents: 1495,
      freeThresholdCents: null,
      provisional: true,
    },
  },
  {
    id: 'international',
    label: 'Internacional',
    countries: ['*'], // catch-all: any other country
    enabled: true,
    rate: {
      id: 'international-standard',
      label: 'Envío internacional',
      amountCents: 2995,
      freeThresholdCents: null,
      provisional: true,
    },
  },
];

/** A whole-day range (inclusive), in business days. */
export interface DayRange {
  min: number;
  max: number;
}

/**
 * Indicative delivery timings in BUSINESS days (Monday to Friday), decided by
 * the owner on 2026-10-13 (docs/plan-seo-fase4.md, D1). The single source for
 * every timing CARO states: the product page line and its prerender, the
 * checkout estimate, legal §18 and the JSON-LD (handlingTime / transitTime /
 * deliveryTime). Never repeat these numbers by hand anywhere else.
 * - `handling`: from the order to the parcel leaving CARO (preparation).
 * - `transit`: carrier time per shipping zone (keys = SHIPPING_ZONES ids).
 * Large Casa pieces (own delivery, customCrate) use the same timings (owner
 * 2026-10-13); pre-order pieces and enquiry products state none.
 */
export const SHIPPING_TIMES: {
  handling: DayRange;
  transit: Record<'es' | 'europe' | 'international', DayRange>;
} = {
  handling: { min: 1, max: 2 },
  transit: {
    es: { min: 1, max: 3 },
    europe: { min: 2, max: 5 },
    international: { min: 4, max: 8 },
  },
};

/** The zones with a stated transit time, in display order (España, Europa, internacional). */
export const TIMED_ZONE_IDS = Object.keys(SHIPPING_TIMES.transit) as (keyof typeof SHIPPING_TIMES.transit)[];

/** Days on which orders are prepared and carriers deliver (schema.org DayOfWeek names). */
export const SHIPPING_BUSINESS_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as const;

/** Carrier transit time of a zone, or undefined for a zone without stated timings. */
export function transitDays(zoneId: string | undefined): DayRange | undefined {
  return zoneId && zoneId in SHIPPING_TIMES.transit
    ? SHIPPING_TIMES.transit[zoneId as keyof typeof SHIPPING_TIMES.transit]
    : undefined;
}

/** Total estimate for a zone (preparation + transit), or undefined without stated timings. */
export function deliveryDays(zoneId: string | undefined): DayRange | undefined {
  const transit = transitDays(zoneId);
  if (!transit) return undefined;
  return { min: SHIPPING_TIMES.handling.min + transit.min, max: SHIPPING_TIMES.handling.max + transit.max };
}

/**
 * Whether a product states delivery timings (visible line + JSON-LD
 * deliveryTime): not for pre-orders (their delay notice rules), enquiry
 * (quotation) products nor sold-out ones (isSoldOut: nothing to deliver).
 */
export function hasDeliveryEstimate(
  p: Pick<Product, 'status' | 'stockQuantity' | 'checkoutMode' | 'metadata'>,
): boolean {
  return p.checkoutMode !== 'enquiry' && p.metadata.preorder !== true && !isSoldOut(p);
}

/** Dictionary lookup for one locale (useI18n().t, or the Worker's translate(locale, ·)). */
export type EstimateTranslate = (key: string) => string;

const fill = (template: string, r: DayRange) =>
  template.replace('{min}', String(r.min)).replace('{max}', String(r.max));

/**
 * Customer-facing delivery estimate, from SHIPPING_TIMES and the dictionary's
 * `shipping.estimate.*` templates (`{min}`/`{max}` placeholders, no numbers in
 * the copy). The templates live in the client dictionary, which src/lib may not
 * import (tsconfig project boundary), so the caller passes its locale's `t`.
 * - `'all'`: the product page line, «Preparación 1-2 días laborables · entrega:
 *   España 1-3 · Europa 2-5 · internacional 4-8».
 * - a zone id: the checkout line for that zone (preparation + transit), e.g.
 *   «Entrega estimada: 2-5 días laborables»; '' for a zone without timings.
 */
export function formatDeliveryEstimate(scope: string, t: EstimateTranslate): string {
  if (scope === 'all') {
    const zones = TIMED_ZONE_IDS.map((id) => fill(t(`shipping.estimate.zone.${id}`), SHIPPING_TIMES.transit[id]));
    return `${fill(t('shipping.estimate.handling'), SHIPPING_TIMES.handling)} · ${t('shipping.estimate.delivery')} ${zones.join(' · ')}`;
  }
  const total = deliveryDays(scope);
  return total ? fill(t('shipping.estimate.total'), total) : '';
}

/**
 * Per-tier multiplier applied to a product's base Spain delivery (SDP).
 * DHL prices fluctuate, so the SDP per product is edited in catalog/products.json
 * (no redeploy); these multipliers are the policy relationship between tiers.
 * Keyed by zone id.
 */
export const DELIVERY_TIER_MULTIPLIER: Record<string, number> = {
  es: 1,
  europe: 1.5,
  international: 2.4,
};

export function tierMultiplier(zoneId: string | undefined): number {
  if (!zoneId) return 1;
  return DELIVERY_TIER_MULTIPLIER[zoneId] ?? 1;
}

export interface ShippingContext {
  destinationCountry: string;
  subtotalCents: number;
  /**
   * If any line item requires a shipping quote (e.g. unique sculpture), the
   * order cannot be auto-priced and must go through enquiry/quotation.
   */
  requiresQuote: boolean;
}

export type ShippingResult =
  | { kind: 'priced'; zone: ShippingZone; shippingCents: number; free: boolean }
  | { kind: 'quote_required'; reason: string }
  | { kind: 'unsupported'; reason: string };

export function findZone(
  country: string,
  zones: ShippingZone[] = SHIPPING_ZONES,
): ShippingZone | undefined {
  const cc = country.toUpperCase();
  // Specific country match wins; otherwise fall back to a catch-all "*" zone.
  return (
    zones.find((z) => z.enabled && z.countries.includes(cc)) ??
    zones.find((z) => z.enabled && z.countries.includes('*'))
  );
}

/**
 * Compute shipping for an order. Beauty products and collectible objects are
 * intentionally NOT treated identically: unique/quote items short-circuit to a
 * quotation flow.
 *
 * `zones` defaults to the built-in config, but the Worker passes zones loaded
 * from D1 (managed via the catalog file) so rates can change without a redeploy.
 */
export function calculateShipping(
  ctx: ShippingContext,
  zones: ShippingZone[] = SHIPPING_ZONES,
): ShippingResult {
  if (ctx.requiresQuote) {
    return {
      kind: 'quote_required',
      reason: 'Order contains an item that requires a shipping quotation.',
    };
  }
  const zone = findZone(ctx.destinationCountry, zones);
  if (!zone) {
    return {
      kind: 'unsupported',
      reason: `Shipping to "${ctx.destinationCountry}" is not available yet.`,
    };
  }
  const { rate } = zone;
  // Free shipping ONLY inside the eurozone, over the threshold. Non-euro Europe
  // and international destinations always pay the zone rate.
  const free =
    isEurozone(ctx.destinationCountry) &&
    ctx.subtotalCents >= FREE_SHIPPING_THRESHOLD_CENTS;
  return {
    kind: 'priced',
    zone,
    shippingCents: free ? 0 : rate.amountCents,
    free,
  };
}
